import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Phone, Mail } from 'lucide-react';
import type { BookingOrgProfile } from '../../lib/bookingApi';

interface LocationState {
  bookingToken: string;
  orgProfile: BookingOrgProfile;
}

export function BookingConfirmationPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  if (!state?.orgProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mist px-4">
        <div className="bg-white rounded-lg shadow-brand-md p-8 text-center max-w-sm">
          <h1 className="text-xl font-semibold text-midnight mb-2">Session Expired</h1>
          <p className="text-slate text-body mb-4">
            Please go back and fill out the booking form again.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/${slug}`)}
            className="text-plum font-medium hover:underline"
          >
            Return to booking form
          </button>
        </div>
      </div>
    );
  }

  const { orgProfile } = state;
  const brandPrimary = orgProfile.bookingConfig?.brandPrimaryColor ?? '#6B2D8B';
  const brandText = orgProfile.bookingConfig?.brandTitleTextColor ?? '#FFFFFF';

  return (
    <div className="min-h-screen bg-mist">
      {/* Header */}
      <header
        className="border-b border-stone px-4 py-4"
        style={{ backgroundColor: brandPrimary, color: brandText }}
      >
        <div className="max-w-lg mx-auto flex items-center gap-3">
          {orgProfile.logoBase64 ? (
            <img
              src={orgProfile.logoBase64}
              alt={orgProfile.name}
              className="w-10 h-10 rounded-md object-contain shrink-0"
            />
          ) : (
            <div
              className="w-10 h-10 rounded-md flex items-center justify-center font-bold text-lg shrink-0"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: brandText }}
            >
              {orgProfile.name.charAt(0)}
            </div>
          )}
          <h1 className="text-lg font-bold leading-tight truncate" style={{ color: brandText }}>{orgProfile.name}</h1>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
        {/* Success card */}
        <section className="bg-white rounded-lg shadow-brand-md p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={36} className="text-green-500" />
          </div>
          <h2 className="text-xl font-bold text-midnight mb-3">
            We&apos;ve received your information!
          </h2>
          <p className="text-body text-slate leading-relaxed">
            Someone from <span className="font-semibold text-midnight">{orgProfile.name}</span> will
            reach out to you soon to discuss your move and provide a detailed estimate.
          </p>
        </section>

        {/* What happens next */}
        <section className="bg-white rounded-lg shadow-brand-sm p-5">
          <h3 className="text-sm font-semibold text-midnight mb-3">What happens next?</h3>
          <ol className="space-y-3 text-sm text-primary-theme">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-plum/10 text-plum font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">1</span>
              <span>Our team reviews your move details</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-plum/10 text-plum font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">2</span>
              <span>We&apos;ll contact you to schedule an on-site or virtual estimate</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-plum/10 text-plum font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">3</span>
              <span>You&apos;ll receive a detailed quote with transparent pricing</span>
            </li>
          </ol>
        </section>

        {/* Contact info */}
        <section className="bg-white rounded-lg shadow-brand-sm p-5">
          <h3 className="text-sm font-semibold text-midnight mb-3">
            Need to reach us sooner?
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm text-primary-theme">
              <Phone size={16} className="text-slate shrink-0" />
              <span>Call {orgProfile.name} directly</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-primary-theme">
              <Mail size={16} className="text-slate shrink-0" />
              <span>Check your email for confirmation details</span>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <div className="text-center py-6">
        <span className="text-xs text-slate">Powered by <span className="font-semibold">IOMI Pro</span></span>
      </div>
    </div>
  );
}
